import { getCoverageLineConfig } from '@common/config';
import { intersectionBy, isEmpty, sortBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { messages } from 'i18n';
import { SelectProduct } from 'types';
import FormAutocomplete from 'components/hookFormComponents/FormAutocomplete/FormAutocomplete';
import { AutocompleteOption } from 'components/hookFormComponents/FormAutocomplete/utils';
import { detailsFormFieldsConfig } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/form-methods/config/detailsFieldsConfig';
import { DetailsFieldsNames } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/form-methods/enums';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { getCoverageLayerLabel } from 'broker/utils';

interface ProductSelectProps {
  initialProductId?: string;
  selectProducts: SelectProduct[];
  isLoading: boolean;
  isDisabled?: boolean;
  // When true, displays all available products regardless of market and coverage lines.
  // When false (default), only shows products from the same market that share coverage lines with the initial product.
  showAllSubmissionProducts?: boolean;
}

type SelectProductOption = AutocompleteOption & { inSubmission: boolean };

const addQuoteMessages = messages.addQuotePage;

export default function ProductSelect({
  initialProductId,
  selectProducts,
  isLoading,
  isDisabled,
  showAllSubmissionProducts = false,
}: ProductSelectProps) {
  const fieldName = detailsFormFieldsConfig[DetailsFieldsNames.InsuranceProductId].name;
  const { setValue } = useFormContext();
  const { partialSubmission } = useSubmissionsWorkspace();

  const getSelectProductCategory = useCallback(
    (selectProduct: SelectProductOption) =>
      selectProduct.inSubmission
        ? addQuoteMessages.selectProductGroups.inSubmission
        : addQuoteMessages.selectMarketGroups.notInSubmission,
    [],
  );

  const getProductLabel = (selectProduct: SelectProduct) =>
    `${selectProduct.marketName} ${selectProduct.label ? `, ${selectProduct.label}` : ''}`;

  const getProductSubLabel = (selectProduct: SelectProduct) => {
    const productCoverageLines = selectProduct.coverageLines
      .map((productCoverageLine) => getCoverageLineConfig(productCoverageLine.coverageLine).text)
      .join(', ');
    const coverageLayerLabel = getCoverageLayerLabel(selectProduct.coverageLayer);
    const isAdmittedLabel = selectProduct.isAdmitted ? addQuoteMessages.admitted : addQuoteMessages.nonAdmitted;
    return `${
      !isEmpty(productCoverageLines) ? `${productCoverageLines} | ` : ''
    } ${coverageLayerLabel} | ${isAdmittedLabel}`;
  };

  useEffect(() => {
    if (initialProductId) {
      setValue(fieldName, initialProductId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProductId]);

  const productOptions = useMemo(() => {
    const initialProduct = initialProductId
      ? selectProducts.find((product) => product.id === initialProductId)
      : undefined;

    const hasSharedCoverageLines = (product: SelectProduct, coverageLines: Array<{ coverageLine: string }>) =>
      intersectionBy(coverageLines, product.coverageLines, 'coverageLine').length > 0;

    const isProductInSameMarket = (product: SelectProduct) => product.marketId === initialProduct?.marketId;

    return sortBy(
      selectProducts
        .filter((product) => {
          if (showAllSubmissionProducts) {
            // If no submission coverage lines, show all products
            if (isEmpty(partialSubmission?.coverageLines)) {
              return true;
            }
            // Always show the initial product if it exists
            if (initialProduct?.id === product.id) {
              return true;
            }
            // Show products that share coverage lines with the submission
            return hasSharedCoverageLines(
              product,
              partialSubmission?.coverageLines?.map((line) => ({ coverageLine: line })) || [],
            );
          }
          // If there is already a selected product, show only products from the same market and that share coverage lines
          return (
            !initialProduct ||
            (isProductInSameMarket(product) && hasSharedCoverageLines(product, initialProduct.coverageLines))
          );
        })
        .map((selectProduct) => ({
          label: getProductLabel(selectProduct),
          subLabel: getProductSubLabel(selectProduct),
          value: selectProduct.id,
          inSubmission: selectProduct.inSubmission,
        })),
      [(product) => !product.inSubmission, 'label'],
    );
  }, [initialProductId, selectProducts, showAllSubmissionProducts, partialSubmission?.coverageLines]);

  return (
    <FormAutocomplete
      isLoading={isLoading}
      disabled={isDisabled}
      options={productOptions}
      groupBy={getSelectProductCategory}
      autoSelect={false}
      {...detailsFormFieldsConfig[DetailsFieldsNames.InsuranceProductId]}
    />
  );
}
